import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { MembershipStatus, sessionInfoSelectors } from '@leanpay/models'
import { cn } from '@leanpay/ui'

import { useAppStore } from 'app/app-store'
import { useGetSessionInfo } from 'library/hooks/query-hooks'

// Types
interface ProgressPage {
    route: string
    progress: number
}

interface ProgressStep {
    id: string
    label: string
    pages: ProgressPage[]
}

type StepStatus = 'completed' | 'active' | 'inactive'

// Constants
const STEP_IDS = {
    TERMS_AND_LOGIN: 'terms-and-login',
    REGISTRATION: 'registration',
    OFFER_CONTRACT: 'offer-contract',
    SUMMARY: 'summary',
} as const

// Types for localization
interface StepLocalizations {
    termsAndLogin: string
    registration: string
    offerAndContract: string
    applicationSummary: string
}

// Step configurations - Based on the ROM image provided
const getStepConfigurations = (
    localizations: StepLocalizations,
    includeTwoFA: boolean,
    includeKYC: boolean,
) => {
    const termsAndLoginStep = {
        id: STEP_IDS.TERMS_AND_LOGIN,
        label: localizations.termsAndLogin,
        pages: [
            { route: '/request-otp', progress: 100 },
            { route: '/login', progress: 100 },
        ],
    } as ProgressStep

    const offerAndContractStep = {
        id: STEP_IDS.OFFER_CONTRACT,
        label: localizations.offerAndContract,
        pages: [
            { route: '/validate-offer', progress: 33.3 },
            { route: '/financial-details', progress: 33.3 },
            { route: '/choose-installment?adjustOffer=true', progress: 33.3 },
            { route: '/offer-confirmation', progress: 66.6 },
            { route: '/sign-contract', progress: 100 },
            { route: '/pep-questionnaire', progress: 100 },
            { route: '/forgot-two-fa-pin', progress: 100 },
        ],
    } as ProgressStep

    const summaryStep = {
        id: STEP_IDS.SUMMARY,
        label: localizations.applicationSummary,
        pages: [
            { route: '/checkout-success', progress: 100 },
            { route: '/checkout-error', progress: 100 },
        ],
    } as ProgressStep

    // Registration step for non-verified users (first time shoppers)
    const registrationPages: ProgressPage[] = []

    // Calculate total steps and progress percentages
    const totalSteps = 2 + (includeTwoFA ? 1 : 0) + (includeKYC ? 2 : 0)
    const stepIncrement = 100 / totalSteps

    let currentStep = 1
    registrationPages.push(
        { route: '/personal-details', progress: stepIncrement * currentStep++ },
        { route: '/address-details', progress: stepIncrement * currentStep++ },
    )

    if (includeTwoFA) {
        registrationPages.push({ route: '/two-fa-pin', progress: stepIncrement * currentStep++ })
    }

    if (includeKYC) {
        registrationPages.push(
            { route: '/verification-info', progress: stepIncrement * currentStep++ },
            { route: '/kyc-verification', progress: 100 },
        )
    } else {
        // If no KYC, make sure the last step is 100%
        registrationPages[registrationPages.length - 1]!.progress = 100
    }

    const registrationStep: ProgressStep = {
        id: STEP_IDS.REGISTRATION,
        label: localizations.registration,
        pages: registrationPages,
    }

    return {
        firstTimeShopper: [termsAndLoginStep, registrationStep, offerAndContractStep, summaryStep],
        returningShopper: [termsAndLoginStep, offerAndContractStep, summaryStep],
    }
}

// Style helpers
const getStepBarClasses = (status: StepStatus) => {
    return cn('h-1 rounded-full flex-1', {
        'bg-lp-red': status === 'completed',
        'bg-lp-dark-label': status === 'inactive',
    })
}

const getStepTextClasses = (status: StepStatus) => {
    return cn('text-xs font-bold mb-0', {
        'text-lp-red': status === 'completed' || status === 'active',
        'text-lp-dark-label': status === 'inactive',
    })
}

// Helper functions
const findCurrentPage = (
    pages: ProgressPage[],
    location: ReturnType<typeof useLocation>,
): ProgressPage | null => {
    const currentPath = location.pathname
    const currentSearch = location.search
    const fullPath = currentPath + currentSearch

    return (
        pages.find((page) => {
            if (page.route.includes('?')) {
                return fullPath === page.route
            } else {
                return currentPath === page.route
            }
        }) ?? null
    )
}

const useProgressCalculation = (
    steps: ProgressStep[],
    location: ReturnType<typeof useLocation>,
) => {
    const getCurrentStepIndex = (): number => {
        return steps.findIndex((step) => findCurrentPage(step.pages, location) !== null)
    }

    const calculateProgressInStep = (stepIndex: number, currentStepIndex: number): number => {
        if (currentStepIndex !== stepIndex) return 0

        const currentStep = steps[stepIndex]
        const currentPage = findCurrentPage(currentStep!.pages, location)

        return currentPage?.progress ?? 0
    }

    const getStepStatus = (stepIndex: number, currentStepIndex: number): StepStatus => {
        if (currentStepIndex === -1) return 'inactive'
        if (stepIndex < currentStepIndex) return 'completed'
        if (stepIndex === currentStepIndex) return 'active'
        return 'inactive'
    }

    return {
        getCurrentStepIndex,
        calculateProgressInStep,
        getStepStatus,
    }
}

const ProgressStepBar = ({
    status,
    progressPercentage,
}: {
    status: StepStatus
    progressPercentage: number
}) => {
    if (status === 'active') {
        return (
            <div
                className="h-1 rounded-full flex-1"
                style={{
                    background: `linear-gradient(to right, #DF1E43 ${progressPercentage}%, #666666 ${progressPercentage}%)`,
                }}
            />
        )
    }

    return <div className={getStepBarClasses(status)} />
}

export const EnhancedProgressBarRom = (): JSX.Element | null => {
    const { t } = useTranslation()
    const location = useLocation()
    const { data: sessionInfo } = useGetSessionInfo()
    const { isFirstTimeShopper, setIsFirstTimeShopper, checkoutData } = useAppStore()

    // In-store-verified customers with a PENDING membership walk the full
    // registration flow, so they get the first-time-shopper steps.
    const isInStoreVerifiedWithPendingMembership =
        Boolean(checkoutData?.isInStore) &&
        sessionInfo !== undefined &&
        sessionInfoSelectors.isVerified(sessionInfo) &&
        sessionInfo.membershipStatus === MembershipStatus.PENDING

    // Save the initial isFirstTimeShopper value to app store when sessionInfo is first received
    if (sessionInfo && isFirstTimeShopper === null) {
        setIsFirstTimeShopper(
            !sessionInfoSelectors.isVerified(sessionInfo) ||
                sessionInfoSelectors.isFirstTimeShopper(sessionInfo) ||
                isInStoreVerifiedWithPendingMembership,
        )
    }

    const includeTwoFA = sessionInfo?.twoFAStatus === 'NOT_SET'
    const includeKYC = sessionInfo ? sessionInfoSelectors.shouldShowKYC(sessionInfo) : false

    const stepConfigurations = getStepConfigurations(
        {
            termsAndLogin: t('common.termsAndLogin'),
            registration: t('common.registration'),
            offerAndContract: t('common.offerAndContract'),
            applicationSummary: t('common.applicationSummary'),
        },
        includeTwoFA,
        includeKYC,
    )
    const steps = isFirstTimeShopper
        ? stepConfigurations.firstTimeShopper
        : stepConfigurations.returningShopper

    const { getCurrentStepIndex, calculateProgressInStep, getStepStatus } = useProgressCalculation(
        steps,
        location,
    )

    const currentStepIndex = getCurrentStepIndex()
    const currentStep = currentStepIndex >= 0 ? steps[currentStepIndex] : null

    // Don't render if we don't have the user type information yet
    if (isFirstTimeShopper === null) {
        return null
    }

    return (
        <div className="w-full mt-0.5 flex flex-col">
            {/* Mobile: Show only current step text */}
            <p className={cn(getStepTextClasses('active'), 'sm:hidden')}>{currentStep?.label}</p>

            {/* Desktop: Show all step labels above progress bars */}
            <div className="hidden sm:flex justify-between items-center gap-1">
                {steps.map((step, index) => {
                    const stepStatus = getStepStatus(index, currentStepIndex)
                    return (
                        <div className="flex flex-1" key={`${step.id}-label`}>
                            <p className={getStepTextClasses(stepStatus)}>{step.label}</p>
                        </div>
                    )
                })}
            </div>

            <div className="flex items-center gap-1">
                {steps.map((step, index) => {
                    const stepStatus = getStepStatus(index, currentStepIndex)
                    const progressPercentage = calculateProgressInStep(index, currentStepIndex)

                    return (
                        <div className="flex items-center gap-1 flex-1" key={step.id}>
                            <ProgressStepBar
                                status={stepStatus}
                                progressPercentage={progressPercentage}
                            />
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
